/**
 * Performance Monitoring Utilities
 *
 * Provides hooks and utilities for measuring and tracking map
 * component performance metrics.
 *
 * Phase 8.1.6: Performance monitoring hooks
 */

import { useRef, useCallback, useEffect, useState } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface PerformanceMetrics {
  /** Frames per second (rolling average) */
  fps: number;
  /** Last frame duration in ms */
  frameTime: number;
  /** Number of frames below 60fps threshold */
  droppedFrames: number;
  /** Memory usage in MB (if available) */
  memoryMB: number | null;
  /** Total render time in ms */
  renderTime: number;
  /** GeoJSON conversion time in ms */
  geoJsonTime: number;
  /** Cluster calculation time in ms */
  clusterTime: number;
  /** Viewport culling time in ms */
  cullingTime: number;
  /** Number of visible sensors */
  visibleSensors: number;
  /** Total sensors */
  totalSensors: number;
}

export interface PerformanceMark {
  name: string;
  startTime: number;
  duration: number | null;
}

export interface PerformanceMonitorResult {
  /** Current performance metrics */
  metrics: PerformanceMetrics;
  /** Start a performance measurement */
  startMeasure: (name: string) => void;
  /** End a performance measurement */
  endMeasure: (name: string) => number;
  /** Record a metric value */
  recordMetric: (name: keyof PerformanceMetrics, value: number) => void;
  /** Get all recorded measurements */
  getMeasurements: () => Map<string, number[]>;
  /** Reset all metrics */
  reset: () => void;
  /** Log current metrics to console */
  logMetrics: () => void;
}

// ============================================================================
// Constants
// ============================================================================

export const PERFORMANCE_THRESHOLDS = {
  /** Target frame time for 60fps */
  FRAME_TIME_TARGET: 16.67,
  /** Warning threshold for frame time */
  FRAME_TIME_WARNING: 33.33, // 30fps
  /** Critical threshold for frame time */
  FRAME_TIME_CRITICAL: 100, // 10fps
  /** Target initial render time for 100 sensors */
  INITIAL_RENDER_100: 500,
  /** Target initial render time for 500 sensors */
  INITIAL_RENDER_500: 1000,
  /** Target data update latency */
  DATA_UPDATE_LATENCY: 100,
  /** Target memory footprint in MB */
  MEMORY_TARGET: 50,
  /** Number of frames to average for FPS */
  FPS_SAMPLE_SIZE: 60,
} as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get current memory usage in MB (if available)
 */
export function getMemoryUsage(): number | null {
  if (typeof performance !== 'undefined' && (performance as any).memory) {
    const memory = (performance as any).memory;
    return Math.round(memory.usedJSHeapSize / (1024 * 1024) * 10) / 10;
  }
  return null;
}

/**
 * Create a performance marker for measuring operations
 */
export function createPerformanceMarker(name: string): {
  start: () => void;
  end: () => number;
  getElapsed: () => number;
} {
  let startTime = 0;

  return {
    start() {
      startTime = performance.now();
    },
    end() {
      const elapsed = performance.now() - startTime;
      return elapsed;
    },
    getElapsed() {
      return performance.now() - startTime;
    },
  };
}

/**
 * Measure execution time of a function
 */
export function measureExecution<T>(
  fn: () => T,
  label: string
): { result: T; time: number } {
  const start = performance.now();
  const result = fn();
  const time = performance.now() - start;

  return { result, time };
}

/**
 * Measure async execution time
 */
export async function measureAsyncExecution<T>(
  fn: () => Promise<T>,
  label: string
): Promise<{ result: T; time: number }> {
  const start = performance.now();
  const result = await fn();
  const time = performance.now() - start;

  return { result, time };
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for monitoring FPS during map interactions
 *
 * @returns FPS monitoring state and controls
 *
 * @example
 * ```typescript
 * const { fps, isMonitoring, start, stop } = useFPSMonitor();
 *
 * useEffect(() => {
 *   start();
 *   return () => stop();
 * }, []);
 *
 * console.log(`Current FPS: ${fps}`);
 * ```
 */
export function useFPSMonitor() {
  const [fps, setFPS] = useState(60);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const frameTimesRef = useRef<number[]>([]);
  const lastFrameTimeRef = useRef(performance.now());
  const rafIdRef = useRef<number | null>(null);

  const measureFrame = useCallback(() => {
    const now = performance.now();
    const frameTime = now - lastFrameTimeRef.current;
    lastFrameTimeRef.current = now;

    // Add to rolling buffer
    frameTimesRef.current.push(frameTime);
    if (frameTimesRef.current.length > PERFORMANCE_THRESHOLDS.FPS_SAMPLE_SIZE) {
      frameTimesRef.current.shift();
    }

    // Calculate average FPS
    const avgFrameTime =
      frameTimesRef.current.reduce((a, b) => a + b, 0) / frameTimesRef.current.length;
    const currentFPS = Math.round(1000 / avgFrameTime);

    setFPS(currentFPS);

    // Continue monitoring
    rafIdRef.current = requestAnimationFrame(measureFrame);
  }, []);

  const start = useCallback(() => {
    if (rafIdRef.current) return;
    setIsMonitoring(true);
    lastFrameTimeRef.current = performance.now();
    frameTimesRef.current = [];
    rafIdRef.current = requestAnimationFrame(measureFrame);
  }, [measureFrame]);

  const stop = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    setIsMonitoring(false);
  }, []);

  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return { fps, isMonitoring, start, stop };
}

/**
 * Hook for comprehensive performance monitoring
 *
 * Tracks frame times, render performance, and component-specific metrics.
 *
 * @returns Performance monitor interface
 *
 * @example
 * ```typescript
 * const { metrics, startMeasure, endMeasure, logMetrics } = usePerformanceMonitor();
 *
 * // Measure an operation
 * startMeasure('geoJsonConversion');
 * const geoJson = convertToGeoJSON(sensors);
 * endMeasure('geoJsonConversion');
 *
 * // Record sensor count
 * recordMetric('visibleSensors', geoJson.features.length);
 * ```
 */
export function usePerformanceMonitor(): PerformanceMonitorResult {
  const marksRef = useRef<Map<string, number>>(new Map());
  const measurementsRef = useRef<Map<string, number[]>>(new Map());
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fps: 60,
    frameTime: 16.67,
    droppedFrames: 0,
    memoryMB: getMemoryUsage(),
    renderTime: 0,
    geoJsonTime: 0,
    clusterTime: 0,
    cullingTime: 0,
    visibleSensors: 0,
    totalSensors: 0,
  });

  const startMeasure = useCallback((name: string) => {
    marksRef.current.set(name, performance.now());
  }, []);

  const endMeasure = useCallback((name: string): number => {
    const startTime = marksRef.current.get(name);
    if (startTime === undefined) {
      return 0;
    }

    const duration = performance.now() - startTime;
    marksRef.current.delete(name);

    // Store measurement
    if (!measurementsRef.current.has(name)) {
      measurementsRef.current.set(name, []);
    }
    const measurements = measurementsRef.current.get(name)!;
    measurements.push(duration);
    // Keep last 100 measurements
    if (measurements.length > 100) {
      measurements.shift();
    }

    // Update relevant metric
    const metricMap: Record<string, keyof PerformanceMetrics> = {
      render: 'renderTime',
      geoJson: 'geoJsonTime',
      cluster: 'clusterTime',
      culling: 'cullingTime',
      frameTime: 'frameTime',
    };

    const metricKey = metricMap[name];
    if (metricKey) {
      setMetrics((prev) => ({ ...prev, [metricKey]: duration }));
    }

    return duration;
  }, []);

  const recordMetric = useCallback((name: keyof PerformanceMetrics, value: number) => {
    setMetrics((prev) => ({ ...prev, [name]: value }));
  }, []);

  const getMeasurements = useCallback(() => {
    return new Map(measurementsRef.current);
  }, []);

  const reset = useCallback(() => {
    marksRef.current.clear();
    measurementsRef.current.clear();
    setMetrics({
      fps: 60,
      frameTime: 16.67,
      droppedFrames: 0,
      memoryMB: getMemoryUsage(),
      renderTime: 0,
      geoJsonTime: 0,
      clusterTime: 0,
      cullingTime: 0,
      visibleSensors: 0,
      totalSensors: 0,
    });
  }, []);

  const logMetrics = useCallback(() => {
    const memoryMB = getMemoryUsage();

    console.log('[Performance] Current Metrics:');
    console.table({
      'FPS': metrics.fps,
      'Frame Time (ms)': metrics.frameTime.toFixed(2),
      'Dropped Frames': metrics.droppedFrames,
      'Memory (MB)': memoryMB ?? 'N/A',
      'Render Time (ms)': metrics.renderTime.toFixed(2),
      'GeoJSON Time (ms)': metrics.geoJsonTime.toFixed(2),
      'Cluster Time (ms)': metrics.clusterTime.toFixed(2),
      'Culling Time (ms)': metrics.cullingTime.toFixed(2),
      'Visible Sensors': metrics.visibleSensors,
      'Total Sensors': metrics.totalSensors,
    });

    // Log measurement averages
    console.log('[Performance] Measurement Averages:');
    measurementsRef.current.forEach((values, name) => {
      const avg = values.reduce((a, b) => a + b, 0) / values.length;
      const min = Math.min(...values);
      const max = Math.max(...values);
      console.log(`  ${name}: avg=${avg.toFixed(2)}ms, min=${min.toFixed(2)}ms, max=${max.toFixed(2)}ms`);
    });
  }, [metrics]);

  // Update memory periodically
  useEffect(() => {
    const intervalId = setInterval(() => {
      const memoryMB = getMemoryUsage();
      if (memoryMB !== null) {
        setMetrics((prev) => ({ ...prev, memoryMB }));
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, []);

  return {
    metrics,
    startMeasure,
    endMeasure,
    recordMetric,
    getMeasurements,
    reset,
    logMetrics,
  };
}

/**
 * Hook for detecting performance issues during render
 *
 * @param thresholdMs - Frame time threshold for warnings
 * @returns Warning handler
 */
export function useRenderWarnings(thresholdMs: number = PERFORMANCE_THRESHOLDS.FRAME_TIME_WARNING) {
  const lastRenderTimeRef = useRef(performance.now());
  const warningsRef = useRef<{ time: number; duration: number }[]>([]);

  const checkRender = useCallback(() => {
    const now = performance.now();
    const renderTime = now - lastRenderTimeRef.current;
    lastRenderTimeRef.current = now;

    if (renderTime > thresholdMs) {
      warningsRef.current.push({ time: now, duration: renderTime });

      // Keep only last 100 warnings
      if (warningsRef.current.length > 100) {
        warningsRef.current.shift();
      }
    }

    return renderTime;
  }, [thresholdMs]);

  const getWarnings = useCallback(() => {
    return [...warningsRef.current];
  }, []);

  const clearWarnings = useCallback(() => {
    warningsRef.current = [];
  }, []);

  return { checkRender, getWarnings, clearWarnings };
}

/**
 * Create a performance report for the map component
 *
 * @param measurements - Map of measurement names to values
 * @param sensorCount - Number of sensors being rendered
 * @returns Performance report object
 */
export function createPerformanceReport(
  measurements: Map<string, number[]>,
  sensorCount: number
): {
  summary: string;
  passed: boolean;
  details: Record<string, { value: number; target: number; passed: boolean }>;
} {
  const details: Record<string, { value: number; target: number; passed: boolean }> = {};

  // Calculate averages
  const getAvg = (name: string) => {
    const values = measurements.get(name);
    if (!values || values.length === 0) return 0;
    return values.reduce((a, b) => a + b, 0) / values.length;
  };

  // Check render time
  const renderTime = getAvg('render');
  const renderTarget = sensorCount <= 100
    ? PERFORMANCE_THRESHOLDS.INITIAL_RENDER_100
    : PERFORMANCE_THRESHOLDS.INITIAL_RENDER_500;
  details['Initial Render'] = {
    value: renderTime,
    target: renderTarget,
    passed: renderTime <= renderTarget,
  };

  // Check frame time
  const frameTime = getAvg('frameTime');
  details['Frame Time'] = {
    value: frameTime,
    target: PERFORMANCE_THRESHOLDS.FRAME_TIME_TARGET,
    passed: frameTime <= PERFORMANCE_THRESHOLDS.FRAME_TIME_TARGET,
  };

  // Check data update
  const updateTime = getAvg('dataUpdate');
  details['Data Update'] = {
    value: updateTime,
    target: PERFORMANCE_THRESHOLDS.DATA_UPDATE_LATENCY,
    passed: updateTime <= PERFORMANCE_THRESHOLDS.DATA_UPDATE_LATENCY,
  };

  // Check memory
  const memory = getMemoryUsage();
  if (memory !== null) {
    details['Memory Usage'] = {
      value: memory,
      target: PERFORMANCE_THRESHOLDS.MEMORY_TARGET,
      passed: memory <= PERFORMANCE_THRESHOLDS.MEMORY_TARGET,
    };
  }

  // Overall pass/fail
  const passed = Object.values(details).every((d) => d.passed);

  // Generate summary
  const summary = passed
    ? `Performance targets met for ${sensorCount} sensors`
    : `Performance issues detected with ${sensorCount} sensors: ` +
      Object.entries(details)
        .filter(([, d]) => !d.passed)
        .map(([name, d]) => `${name} (${d.value.toFixed(1)}ms > ${d.target}ms)`)
        .join(', ');

  console.log(`[Performance] Report: ${summary}`);

  return { summary, passed, details };
}

export default {
  getMemoryUsage,
  createPerformanceMarker,
  measureExecution,
  measureAsyncExecution,
  useFPSMonitor,
  usePerformanceMonitor,
  useRenderWarnings,
  createPerformanceReport,
  PERFORMANCE_THRESHOLDS,
};
